import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FaChevronRight } from 'react-icons/fa';
import GroupPopover from '../popovers/GroupPopover';

const ICON_SIZE = 18;

/**
 * Grupo de menu com submenu. Comporta-se diferente conforme `collapsed`:
 *   expandido  → dropdown inline com chevron rotacionando
 *   collapsed  → só ícone; ao clicar abre GroupPopover via portal ao lado
 *
 * No modo collapsed, o popover é renderizado via portal no <body> pra
 * escapar do overflow do <Nav>. O anchorRef permite calcular a posição.
 */
function MenuGroup({
   group,
   visibleChildren,
   collapsed,
   isOpen,
   hasActiveChild,
   isActive,
   onToggle,
   onNavigate = undefined,
   popoverOpen,
   onOpenPopover,
   onClosePopover,
}) {
   const { key, label, icon: IconRoot } = group;
   const btnRef = useRef(null);

   if (collapsed) {
      return (
         <div className="menu-group">
            <button
               ref={btnRef}
               type="button"
               className={`menu-group-btn ${hasActiveChild ? 'has-active' : ''} ${popoverOpen ? 'open' : ''}`}
               title={label}
               onClick={() => onOpenPopover(key)}
            >
               <div className="menu-icon">
                  <IconRoot size={ICON_SIZE} />
               </div>
            </button>

            {popoverOpen && (
               <GroupPopover
                  group={{ key, label }}
                  visibleChildren={visibleChildren}
                  isActive={isActive}
                  onNavigate={onNavigate}
                  onClose={onClosePopover}
                  anchorRef={btnRef}
               />
            )}
         </div>
      );
   }

   return (
      <div className="menu-group">
         <button
            type="button"
            className={`menu-group-btn ${isOpen ? 'open' : ''} ${hasActiveChild ? 'has-active' : ''}`}
            onClick={() => onToggle(key)}
            title={label}
         >
            <div className="menu-icon">
               <IconRoot size={ICON_SIZE} />
            </div>
            <span className="menu-label">{label}</span>
            <div className={`menu-chevron ${isOpen ? 'rotate' : ''}`}>
               <FaChevronRight size={12} />
            </div>
         </button>

         <div className={`submenu-wrapper ${isOpen ? 'open' : ''}`}>
            <div className="submenu">
               {visibleChildren.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.to);
                  return (
                     <Link
                        key={item.to}
                        to={item.to}
                        title={item.label}
                        className={`submenu-link ${active ? 'active' : ''}`}
                        onClick={onNavigate}
                     >
                        <div className="submenu-icon">
                           <Icon size={15} />
                        </div>
                        <span className="submenu-label">{item.label}</span>
                        {item.newItem && <span className="new-badge">Novo!</span>}
                        {active && <div className="active-indicator" />}
                     </Link>
                  );
               })}
            </div>
         </div>
      </div>
   );
}

MenuGroup.propTypes = {
   group: PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
   }).isRequired,
   visibleChildren: PropTypes.arrayOf(
      PropTypes.shape({
         to: PropTypes.string.isRequired,
         label: PropTypes.string.isRequired,
         icon: PropTypes.elementType.isRequired,
         newItem: PropTypes.bool,
      })
   ).isRequired,
   collapsed: PropTypes.bool.isRequired,
   isOpen: PropTypes.bool.isRequired,
   hasActiveChild: PropTypes.bool.isRequired,
   isActive: PropTypes.func.isRequired,
   onToggle: PropTypes.func.isRequired,
   onNavigate: PropTypes.func,
   popoverOpen: PropTypes.bool.isRequired,
   onOpenPopover: PropTypes.func.isRequired,
   onClosePopover: PropTypes.func.isRequired,
};

export default MenuGroup;
